import {
	ReactElement,
	useCallback,
	useMemo
} from "react";
import {
	GenericNavigationPanelProps,
	SubNavigation
} from "../../SubNavigation";
import { AppType } from "client/Api/Apps/common";
import { localize } from "client_legacy/ndaw/culture";
import { useShouldStartLoadingData } from "../useShouldStartLoadingData";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { SidebarCalendar } from "client/Features/Calendar/CalendarSidebarContent/SidebarCalendar";
import { calendarRouting } from "client/Features/Calendar/CalendarPage";
import { createBrowserNavigationUrl } from "client/Navigation";
import { useToday, useUserTimezone } from "client/Hooks";
import { TimeZone } from "client/DateAndTime/timezones";
import { DateOnly } from "client/DateAndTime/dateOnly";
import { convertToDateOnly } from "client/DateAndTime/dateUtils";

export type CalendarPanelProps = GenericNavigationPanelProps;

export const CalendarPanel = (props: CalendarPanelProps): ReactElement | null => {
	const {
		isVisible,
		isPinned,
		isActive
	} = props;

	const userTimezone: TimeZone = useUserTimezone();

	const shouldStartLoadingData: boolean = useShouldStartLoadingData(isPinned, isVisible);
	const isRenderedByPage: boolean = isActive;

	const onChangeHandler = useCallback(
		(value: DateOnly): void => {
			const url: string = createBrowserNavigationUrl(
				calendarRouting.page({ queryParams: { date: value.toISOString() }})
			);

			navigateTo(url);
		},
		[]
	);

	const today: Date = useToday({ timezone: userTimezone });

	const date = useMemo(
		(): DateOnly => convertToDateOnly(today, userTimezone),
		[today, userTimezone]
	);

	if (isRenderedByPage) {
		return null;
	}

	const renderContent = (): ReactElement | null => {
		if (!shouldStartLoadingData) {
			return <SubNavigation.LoadingIndicator />;
		}

		return (
			<SidebarCalendar
				date={date}
				onChange={onChangeHandler}
				timezone={userTimezone}
			/>
		);
	};

	return (
		<>
			<SubNavigation.Header
				kind={AppType.Office365Calendar}
				title={localize("_Calendar")}
			/>
			<SubNavigation.Body>
				{renderContent()}
			</SubNavigation.Body>
		</>
	);
};
CalendarPanel.displayName = "CalendarPanel";